<template>
  <!-- Hero Section -->
  <section class="py-10 bg-gradient-to-r from-blue-600 to-indigo-700">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <!-- Main Heading -->
      <h1 class="text-4xl lg:text-6xl font-bold text-white mb-6">
        ระบบอบรมออนไลน์ในองค์กร
        <span class="text-blue-200 text-2xl block mt-2">ครบเครื่องเรื่องการเรียนการสอน</span>
      </h1>
      <!-- Subtitle -->
      <p class="text-xl text-blue-100 max-w-3xl mx-auto mb-0">
        ระบบ KM ที่ใช้งานง่าย ครบครันทุกฟังก์ชัน
      </p>
    </div>
  </section>

  <!-- Dynamic Feature Sections -->
  <section v-for="(feature, index) in features" :key="feature.id" 
           class="relative py-16 overflow-hidden">
    <!-- Background Image with Color Overlay -->
    <div class="absolute inset-0">
      <img :src="feature.backgroundImage" :alt="feature.backgroundAlt" class="w-full h-full object-cover">
      <div class="absolute inset-0" :class="feature.overlay"></div>
    </div>
    
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-12 items-center">
        <!-- Content -->
        <div :class="feature.contentOrder">
          <span class="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-4 py-2 rounded-full font-semibold mb-6">
            <i :class="feature.icon + ' text-lg mr-3'"></i>
            {{ feature.badge }}
          </span>
          <h2 class="text-4xl lg:text-5xl font-bold mb-6" :class="feature.titleColor">
            <span>{{ feature.title }}</span>
            <span class="text-blue-600 block mt-1 text-3xl lg:text-4xl" v-if="feature.subtitle">{{ feature.subtitle }}</span>
          </h2>
          <p class="text-xl mb-8 leading-relaxed" :class="feature.textColor">
            {{ feature.description }}
          </p>
          <ul class="space-y-4 mb-8">
            <li v-for="item in feature.features" :key="item" class="flex items-center">
              <div class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <span :class="feature.listColor">{{ item }}</span>
            </li>
          </ul>
          <!-- <button class="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
            {{ feature.buttonText }}
          </button> -->
        </div>
        
        <!-- Image -->
        <div :class="feature.imageOrder" class="relative z-10">
          <div class="bg-gradient-to-br from-blue-200 to-indigo-200 rounded-2xl shadow-2xl p-2 transform rotate-0 hover:rotate-3 transition-transform duration-500 overflow-hidden">
            <!-- Browser Frame -->
            <div class="bg-gray-100 rounded-t-xl px-4 py-2 flex items-center space-x-2">
              <div class="flex space-x-1">
                <div class="w-3 h-3 bg-red-400 rounded-full"></div>
                <div class="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div class="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
              <div class="flex-1 bg-white rounded mx-4 px-2 py-1">
                <span class="text-xs text-gray-500">{{ feature.url }}</span>
              </div>
            </div>
            <!-- Content Area -->
            <div class="relative">
              <img :src="feature.image" :alt="feature.imageAlt" class="w-full h-80 object-cover">
              <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                <div class="text-white p-4">
                  <div class="flex items-center space-x-2 mb-2">
                    <i :class="feature.icon + ' text-xl text-blue-400'"></i>
                    <span class="font-semibold">{{ feature.imageTitle }}</span>
                  </div>
                  <p class="text-sm opacity-90">{{ feature.imageDescription }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Floating Jigsaw Element (replaces circle) -->
          <div class="absolute -bottom-20" :class="feature.floatingPosition">
            <Jigsaw :size="110"
                    :gradient="jigsawGradientById[feature.id]"
                    :icon="feature.floatingIcon"
                    icon-color="#2563eb"
                    icon-size="1.5rem"
                    :circle-size="38" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import Jigsaw from '~/components/Jigsaw.vue'

// Gradient mapping for floating jigsaw by feature id
const jigsawGradientById = {
  dashboard: 'linear-gradient(135deg, #60a5fa, #a78bfa)', // blue -> purple
  course: 'linear-gradient(135deg, #60a5fa, #06b6d4)',    // blue -> cyan
  whitelabel: 'linear-gradient(135deg, #f59e0b, #ef4444)', // amber -> rose
  api: 'linear-gradient(135deg, #34d399, #06b6d4)'        // green -> cyan
}

// Features data array
const features = ref([
  {
    id: 'dashboard',
    badge: 'แดชบอร์ดและการวิเคราะห์',
    title: 'ติดตามผลการเรียนรู้',
    subtitle: 'แบบเรียลไทม์',
    description: 'ดูสถิติการเรียน กราฟความคืบหน้า และรายงานต่างๆ ได้ในหน้าเดียว ช่วยให้คุณรู้ว่าผู้เรียนเป็นอย่างไร',
    features: [
      'ติดตามความคืบหน้าแบบเรียลไทม์',
      'รายงานปรับเองได้และส่งออกข้อมูล',
      'การวิเคราะห์การเรียนรู้เชิงลึก'
    ],
    buttonText: 'ดูตัวอย่างแดชบอร์ด',
    backgroundImage: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755239931823.jpg',
    backgroundAlt: 'พื้นหลังการวิเคราะห์',
    overlay: 'bg-gradient-to-r from-blue-50/85 via-gray-50/90 to-blue-100/85',
    image: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755240165179.jpg',
    imageAlt: 'แดชบอร์ดการวิเคราะห์',
    imageTitle: 'แดชบอร์ดการวิเคราะห์',
    imageDescription: 'ภาพข้อมูลแบบเรียลไทม์',
    url: 'dashboard.kmbuilder.com',
    icon: 'fas fa-chart-bar',
    contentOrder: '',
    imageOrder: '',
    titleColor: 'text-gray-900',
    textColor: 'text-gray-600',
    listColor: 'text-gray-700',
    floatingPosition: '-right-8',
    floatingGradient: 'from-blue-400/20 to-purple-400/20',
    floatingIcon: 'fas fa-chart-line',
    floatingIconColor: 'text-blue-500'
  },
  {
    id: 'course',
    badge: 'จัดการคอร์ส',
    title: 'สร้างคอร์สเรียน',
    subtitle: 'ได้อย่างง่ายดาย',
    description: 'สร้างคอร์สแบบลากวาง ใส่วิดีโอ รูปภาพ หรือเอกสารได้เลย ไม่ต้องมีความรู้เทคนิค',
    features: [
      'เครื่องมือสร้างคอร์สแบบลากวาง',
      'รองรับเนื้อหาหลายรูปแบบ',
      'แบบทดสอบและประเมินผลแบบโต้ตอบ'
    ],
    buttonText: 'ลองสร้างคอร์ส',
    backgroundImage: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755239848086.jpg',
    backgroundAlt: 'พื้นหลังการศึกษา',
    overlay: 'bg-gradient-to-r from-white/85 via-purple-50/90 to-pink-50/85',
    image: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755240164855.jpg',
    imageAlt: 'ตัวสร้างคอร์ส',
    imageTitle: 'เครื่องมือสร้างคอร์ส',
    imageDescription: 'อินเทอร์เฟซแบบลากวาง',
    url: 'courses.kmbuilder.com',
    icon: 'fas fa-graduation-cap',
    contentOrder: 'order-1 lg:order-2',
    imageOrder: 'order-2 lg:order-1',
    titleColor: 'text-gray-900',
    textColor: 'text-gray-600',
    listColor: 'text-gray-700',
    floatingPosition: '-left-8',
    floatingGradient: 'from-blue-400/20 to-cyan-400/20',
    floatingIcon: 'fas fa-book-open',
    floatingIconColor: 'text-blue-500'
  },
  {
    id: 'whitelabel',
    badge: 'โซลูชัน White Label',
    title: 'ปรับแต่งแบรนด์',
    subtitle: 'ให้เป็นของคุณ',
    description: 'เปลี่ยนสี โลโก้ และธีมให้ตรงกับแบรนด์ของคุณ ทำให้ดูเหมือนคุณสร้างระบบขึ้นมาเอง',
    features: [
      'โดเมนของคุณเองและ SSL',
      'สีและตัวอักษรตามแบรนด์',
      'โลโก้และแม่แบบอีเมลกำหนดเอง'
    ],
    buttonText: 'ดูตัวอย่างการปรับแต่ง',
    backgroundImage: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755239715963.jpg',
    backgroundAlt: 'พื้นหลังการออกแบบ',
    overlay: 'bg-gradient-to-r from-gray-900/85 via-yellow-900/80 to-orange-900/85',
    image: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755240164377.jpg',
    imageAlt: 'การปรับแต่งแบรนด์',
    imageTitle: 'ปรับแต่งแบรนด์',
    imageDescription: 'แบรนด์ของคุณ สไตล์ของคุณ',
    url: 'brand.kmbuilder.com',
    icon: 'fas fa-palette',
    contentOrder: '',
    imageOrder: '',
    titleColor: 'text-white',
    textColor: 'text-gray-300',
    listColor: 'text-gray-300',
    floatingPosition: '-right-8',
    floatingGradient: 'from-blue-400/20 to-cyan-400/20',
    floatingIcon: 'fas fa-paint-brush',
    floatingIconColor: 'text-blue-600'
  },
  {
    id: 'api',
    badge: 'เชื่อมต่อ API',
    title: 'เชื่อมต่อระบบ',
    subtitle: 'ได้ไม่จำกัด',
    description: 'ต่อกับระบบอื่นๆ ที่คุณใช้อยู่ได้ง่ายๆ ผ่าน API มีเอกสารครบ ไม่ต้องกังวลเรื่องเทคนิค',
    features: [
      'รองรับ RESTful และ GraphQL',
      'Webhooks และเหตุการณ์แบบเรียลไทม์',
      'คู่มือเอกสารครบถ้วน'
    ],
    buttonText: 'ดูเอกสาร API',
    backgroundImage: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755239665863.jpg',
    backgroundAlt: 'พื้นหลังเทคโนโลยี',
    overlay: 'bg-gradient-to-r from-blue-100/85 via-indigo-50/90 to-blue-200/85',
    image: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1755240165412.jpg',
    imageAlt: 'การเชื่อมต่อ API',
    imageTitle: 'การเชื่อมต่อ API',
    imageDescription: 'รองรับ RESTful และ GraphQL',
    url: 'api.kmbuilder.com',
    icon: 'fas fa-plug',
    contentOrder: 'order-1 lg:order-2',
    imageOrder: 'order-2 lg:order-1',
    titleColor: 'text-gray-900',
    textColor: 'text-gray-600',
    listColor: 'text-gray-700',
    floatingPosition: '-left-8',
    floatingGradient: 'from-blue-400/20 to-indigo-400/20',
    floatingIcon: 'fas fa-code',
    floatingIconColor: 'text-blue-500'
  }
])
</script>

<style scoped>
/* Any component-specific styles can be added here */
</style>
